/**
 * 家长端「批量任务」面板 —— **只负责"布置"**一个任务。
 *
 * ## 用户原话（验收标准）
 *
 * > "我布置听写的时候，比如选择了小学二年级上册的词语（可能有一两百个），
 * >  但小朋友每次不可能都能够听写一两百个。因此，我可以设置他每次听写多少个，
 * >  比如设置为每次听写 10 个。……我可以一次性布置一本书或者几本书的听写任务，
 * >  他可以分次去完成。"
 * > "孩子不能结束整个任务，家长可以结束、暂停或删除整个任务。"
 *
 * ## 2026-10-29 改：任务**列表**从这一页搬走了
 *
 * 用户原话：
 *
 * > "听写任务已挪到「听写历史」那里，那你批量任务下面的「听写任务」就可以去掉了，
 * >  对不对？没必要在两个地方都出现同一个东西嘛。"
 *
 * 于是分工变成：
 *
 * | 页签 | 只做什么 |
 * | --- | --- |
 * | **批量任务**（本面板） | **布置**一个新任务（范围 / 每次几个 / 顺序 / 节奏 / 名字） |
 * | **听写历史** | 任务 + 它的每一批（分组、默认折叠）+ 进度/错词 + 暂停/恢复/结束/删除 |
 *
 * 所以这里**不再有任务列表**，也没有列表上的那些按钮（它们在历史页的组头上，
 * 见 `AssignmentAdminActions`）。布置成功后直接跳到「听写历史」——那里能看到
 * 刚建好的任务（哪怕它还没有任何一批）。
 *
 * ## 三态（与全仓一致）
 *
 * 课本列表 `=== null` = **这份数据没拿到**（不是"没有课本"）；查询失败**明说**并给重试。
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardHeader, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { ErrorState } from '../ui/Bits';
import { useToast } from '../ui/Toast';
import { IconAlertTriangle, IconBookOpen, IconPlus } from '../ui/Icons';
import { toErrorMessage } from '../../lib/api/error';
import { listTextbooks, semesterLabel, type TextbookView } from '../../lib/api/dictation';
import * as assignmentApi from '../../lib/api/dictationAssignments';
import { TempoForm } from './DictationTempoForm';
import {
  DEFAULT_DICTATION_TEMPO,
  GRADES,
  describeTextbookLabel,
  gradeLabel,
  type DictationTempoConfig,
} from '@fht/shared';

export interface DictationAssignmentsPanelProps {
  /** `students.id` */
  studentId: string;
  studentNickname: string;
  /** 这个孩子的年级（分册默认值 / 标题兜底）。 */
  defaultGradeCode: string;
  canEdit: boolean;
  /** 面板被切换成"当前页签"时才取数据（省掉一次无用的请求）。 */
  active: boolean;
  /** 布置成功之后：页面据此切到「听写历史」并展开这个新任务。 */
  onCreated?: (assignment: assignmentApi.AssignmentView) => void;
}

/** 范围选法：选具体的书，或者按年级 + 学期兜底。 */
type ScopeMode = 'books' | 'grade';

export function DictationAssignmentsPanel({
  studentId,
  studentNickname,
  defaultGradeCode,
  canEdit,
  active,
  onCreated,
}: DictationAssignmentsPanelProps) {
  const toast = useToast();

  // ---------------------------------------------------------------- 数据
  const textbooksQuery = useQuery({
    queryKey: ['dictation-assignments', studentId, 'textbooks'] as const,
    queryFn: () => listTextbooks({ kind: 'parent', studentId }),
    enabled: active && studentId !== '',
  });

  const textbooks = textbooksQuery.data?.textbooks ?? null;

  // ---------------------------------------------------------------- 表单
  const [scopeMode, setScopeMode] = useState<ScopeMode>('books');
  const [selectedBookIds, setSelectedBookIds] = useState<string[]>([]);
  const [gradeCode, setGradeCode] = useState(defaultGradeCode);
  const [semester, setSemester] = useState('上');
  const [batchSize, setBatchSize] = useState('10');
  const [shuffle, setShuffle] = useState(false);
  const [title, setTitle] = useState('');
  const [tempo, setTempo] = useState<DictationTempoConfig>({ ...DEFAULT_DICTATION_TEMPO });
  const [formError, setFormError] = useState<string | null>(null);

  /**
   * 可选的册：后端返回的课本列表已经是一册一行（出版社 + 年级 + 学期）。
   * 按"年级 + 学期"排一遍，家长找"二年级上册"时不用逐条读。
   */
  const bookOptions = useMemo(() => {
    const rows = textbooks ?? [];
    return rows.slice().sort((a, b) => {
      if (a.gradeCode !== b.gradeCode) return a.gradeCode.localeCompare(b.gradeCode);
      if (a.semester !== b.semester) return a.semester.localeCompare(b.semester);
      return a.name.localeCompare(b.name);
    });
  }, [textbooks]);

  const parsedBatchSize = useMemo(() => {
    const text = batchSize.trim();
    if (text === '') return null;
    const value = Number(text);
    if (!Number.isInteger(value) || value < 1 || value > 200) return null;
    return value;
  }, [batchSize]);

  const createMutation = useMutation({
    mutationFn: async () => {
      if (parsedBatchSize === null) {
        throw new Error('「每次听几个」要填 1~200 之间的整数。');
      }
      if (scopeMode === 'books' && selectedBookIds.length === 0) {
        throw new Error('请至少勾选一本书 —— 或者切到「按年级 + 学期」。');
      }
      return assignmentApi.createAssignment(studentId, {
        title: title.trim(),
        ...(scopeMode === 'books'
          ? { textbookIds: selectedBookIds }
          : { gradeCode, semester }),
        batchSize: parsedBatchSize,
        shuffle,
        tempo,
      });
    },
    onSuccess: (assignment) => {
      toast.showToast(
        `已布置「${assignment.title}」，范围里共 ${assignment.progress.totalWords ?? '—'} 个词`,
        'success',
      );
      setFormError(null);
      setTitle('');
      /** 交给页面切页签：任务在「听写历史」里（哪怕还没有任何一批）也能看到。 */
      onCreated?.(assignment);
    },
    onError: (err: unknown) => setFormError(toErrorMessage(err)),
  });

  // ---------------------------------------------------------------- 渲染
  if (!canEdit) {
    return (
      <Card className="bg-amber-50">
        <p className="text-sm text-amber-800">
          你是「仅查看」权限：能看到 {studentNickname} 的任务与进度，但不能布置或管理任务。
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="布置一个批量任务"
          description={`把范围、每次几个、顺序一次定好，${studentNickname} 每次点「开始听写」就往下听一批 —— 不用大人陪着。布置好之后在「听写历史」里管理它。`}
        />

        <div className="space-y-4">
          {/* ---------- 范围 ---------- */}
          <div>
            <p className="text-xs font-medium text-slate-500">范围怎么选</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {(
                [
                  { value: 'books', label: '选具体的书（推荐）' },
                  { value: 'grade', label: '按年级 + 学期' },
                ] as const
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={scopeMode === option.value}
                  onClick={() => setScopeMode(option.value)}
                  className={
                    scopeMode === option.value
                      ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                      : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {scopeMode === 'books' ? (
            textbooksQuery.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : textbooksQuery.isError || textbooks === null ? (
              <ErrorState
                message={
                  textbooksQuery.isError
                    ? `没拿到课本列表：${toErrorMessage(textbooksQuery.error)}`
                    : '没拿到课本列表（后端返回的字段名与预期不一致）'
                }
                onRetry={() => void textbooksQuery.refetch()}
                compact
              />
            ) : bookOptions.length === 0 ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                字表里还没有课本 —— 先去「工具 → 语文听写」建一册、录入词，再回来布置任务。
              </p>
            ) : (
              <div>
                <p className="text-xs font-medium text-slate-500">
                  勾选这次要听写的书（可以多本，共 {selectedBookIds.length} 本）
                </p>
                <ul className="mt-1 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {bookOptions.map((book: TextbookView) => {
                    const checked = selectedBookIds.includes(book.id);
                    return (
                      <li key={book.id}>
                        <label
                          className={
                            checked
                              ? 'flex items-center gap-2 rounded-xl border border-brand-400 bg-brand-50 px-2 py-2 text-sm text-brand-800'
                              : 'flex items-center gap-2 rounded-xl border border-slate-200 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50'
                          }
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300"
                            checked={checked}
                            onChange={() =>
                              setSelectedBookIds((prev) =>
                                checked
                                  ? prev.filter((item) => item !== book.id)
                                  : [...prev, book.id],
                              )
                            }
                          />
                          <span className="min-w-0 flex-1 truncate">
                            {describeTextbookLabel(book)}
                          </span>
                          <span className="shrink-0 text-[11px] text-slate-400">
                            {book.wordCount === null ? '—' : `${book.wordCount} 词`}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label
                  className="block text-sm font-medium text-slate-700"
                  htmlFor="assignment-grade"
                >
                  年级
                </label>
                <select
                  id="assignment-grade"
                  className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900"
                  value={gradeCode}
                  onChange={(event) => setGradeCode(event.target.value)}
                >
                  <option value={defaultGradeCode}>{gradeLabel(defaultGradeCode)}（这个孩子）</option>
                  {GRADES.map((grade) => (
                    <option key={grade.code} value={grade.code}>
                      {gradeLabel(grade.code)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-slate-700"
                  htmlFor="assignment-semester"
                >
                  学期
                </label>
                <select
                  id="assignment-semester"
                  className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900"
                  value={semester}
                  onChange={(event) => setSemester(event.target.value)}
                >
                  {['上', '下', '不分'].map((value) => (
                    <option key={value} value={value}>
                      {semesterLabel(value)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* ---------- 每次几个 + 顺序 ---------- */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label
                className="block text-sm font-medium text-slate-700"
                htmlFor="assignment-batch-size"
              >
                每次听写几个
              </label>
              <input
                id="assignment-batch-size"
                className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                inputMode="numeric"
                value={batchSize}
                placeholder="例如 10"
                onChange={(event) => setBatchSize(event.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500">
                1~200。最后一批剩几个就听几个。
                {parsedBatchSize === null && batchSize.trim() !== '' && (
                  <span className="ml-1 text-rose-600">这个数不合法。</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">顺序</p>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {(
                  [
                    { value: false, label: '按课本顺序' },
                    { value: true, label: '每次随机' },
                  ] as const
                ).map((option) => (
                  <button
                    key={String(option.value)}
                    type="button"
                    aria-pressed={shuffle === option.value}
                    onClick={() => setShuffle(option.value)}
                    className={
                      shuffle === option.value
                        ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                        : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {shuffle
                  ? '每一批都从还没听过的词里随机取 —— 同一批内不重复。'
                  : '跟着课本一行一行往下听（与字表里的行序一致）。'}
              </p>
            </div>
          </div>

          {/* ---------- 节奏 ---------- */}
          <div>
            <p className="mb-1 text-sm font-medium text-slate-700">
              听写节奏（这三项决定孩子来不来得及写）
            </p>
            <TempoForm
              tempo={tempo}
              wordCount={parsedBatchSize}
              disabled={!canEdit || createMutation.isPending}
              onChange={(patch) => setTempo((prev) => ({ ...prev, ...patch }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="assignment-title">
              任务名字（留空按范围自动起）
            </label>
            <input
              id="assignment-title"
              className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              value={title}
              maxLength={64}
              placeholder="例如：二年级上册 · 词语听写"
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          {formError !== null && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
            >
              <span className="mt-0.5 shrink-0">
                <IconAlertTriangle size={16} />
              </span>
              <span>{formError}</span>
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              loading={createMutation.isPending}
              disabled={!canEdit}
              icon={<IconPlus size={18} />}
              onClick={() => {
                setFormError(null);
                createMutation.mutate();
              }}
            >
              布置这个任务
            </Button>
            <span className="text-xs text-slate-500">
              布置好之后到「听写历史」里看它 —— {studentNickname} 点「开始听写」就往下听。
            </span>
          </div>
        </div>
      </Card>

      <Card className="bg-slate-50">
        <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <IconBookOpen size={18} />
          任务和每一批都在「听写历史」里：进度、错词、暂停 / 结束 / 删除都在那儿，这里只负责布置。
        </p>
      </Card>
    </div>
  );
}
